/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import _ from 'lodash';
import isEqual from 'lodash.isequal';
import isNil from 'lodash.isnil';
import {error} from '@ion/core/src/logger/logger';
import {loadHeartBeatInfo} from '@ion/core/src/services/Connect';
import {
  Device,
  DeviceFiles,
  GenericObject,
  Group,
  IdReport
} from '../types/entities';
import {parseValueWithKey} from './StringUtil';

export enum MDTCategories {
  MDT = 'MDT',
  CAMERA = 'Camera'
};

export enum LMUCategories {
  LMU = 'LMU'
};

export enum DevicePlatforms {
  FIRMWARE = 'LMU Firmware',
  CONFIG = 'LMU Config',
  PEG2 = 'LMU PEG2'
};

export enum VbusFirmwareMode {
  JPOD = 'JPOD',
  OBD = 'OBD',
  VBU = 'VBU'
};

const VALUES = ['OBD_TYPE_2', 'JPOD2'];

/**
 * @function getSignalStrength
 *
 * Takes a standard RSSI number (from -26 to -100) and returns
 * a signalStrength number from 0 to 4
 */
export const getSignalStrength = (signal) => {
  if (signal >= -67) return 4;
  if (signal >= -70 && signal < -67) return 3;
  if (signal >= -80 && signal < -70) return 2;
  if (signal >= -90 && signal < -80) return 1;
  return 0;
};

/**
 * Determines if the provided device is an MDT (Mobile Device Tablet) device.
 * @param device the device to evaluate (containing a category)
 * @returns if the provided device is an MDT device
 */
export const isMDTDevice = (device: Device = {}): boolean => {
  const category = device?.category;
  if (category) {
    return !!MDTCategories[(Array.isArray(category) ? category?.[0] : category)];
  }
  return false;
};

/**
 * Determines if the provided device is an LMU device.
 * @param device the device to evaluate (containing a category)
 * @returns if the provided device is an LMU device
 */
export const isLMUDevice = (device: Device = {}): boolean => {
  const category = device?.category;
  if (category) {
    return !!LMUCategories[(Array.isArray(category) ? category?.[0] : category)];
  }
  return false;
};

/**
 * Given a populated device, determines the respective identifier based on the device's type fields (such as category &
 * architecture) to return identifiers (such as esn, serial number, etc).
 *
 * If the requested identifier is unavailable or not present on the given device it returns an empty string.
 *
 * @param device The device to determine the identifier of.
 * @returns A string device identifer.
 */
export const getDeviceIdentifierFromType = (device: Device = {}): string => {
  let identifier;
  if (isMDTDevice(device)) {
    identifier = device?.serialNumber;
  } else {
    identifier = device?.esn;
  }
  return identifier || '';
};

// Parses a device and returns the id report's vin information.
export const getIdReportVin = (device: Device, defaultValue = ''): string => {
  const vin = getDeviceIdReport(device)?.extensions?.['VIN-INFO']?.[0];
  if (vin) {
    return parseValueWithKey({source: vin, key: 'VIN=', endingDelimiter: ',', customValueLength: 17});
  }
  return defaultValue;
};

// Given a device, will return the inner id report if available. Otherwise undefined.
export const getDeviceIdReport = (device: Device): IdReport => device?.idReport?.device?.idReport;

const REGULAR_NUMBER_REGEX =  /^\d+$/;
const IP_ADDRESS_REGEX     = /^(?!0)(?!.*\.$)((1?\d?\d|25[0-5]|2[0-4]\d)(\.|$)){4}$/;

/**
 * Determines if the given esn is valid.
 *
 * @param esn the esn to validate
 * @returns true if valid, false otherwise
 */
export const isValidESN = (esn: string) => REGULAR_NUMBER_REGEX.test(esn);

/**
 * Determines if the given ipAddress is valid.
 *
 * @param ipAddress the ipAddress to validate
 * @returns true if valid, false otherwise
 */
export const isValidIPAddress = (ipAddress: string) => IP_ADDRESS_REGEX.test(ipAddress);

export const PLATFORM_IDS = {
  firmware                         : 365,
  config                           : 366,
  peg2                             : 373,
  jpodFirmware                     : 374,
  jpodConfig                       : 375,
  hostedAppFirmware                : 381,
  hostedAppConfig                  : 382,
  hostedAppFirmwareFirstPeripheral : 383,
  hostedAppConfigFirstPeripheral   : 384,
  hostedAppFirmware2ndPeripheral   : 385,
  hostedAppConfig2ndPeripheral     : 386,
  hostedAppV2Firmware              : 387,
  hostedAppApnList                 : 388,
  hostedAppV3Firmware              : 389,
  hostedAppV4Firmware              : 390,
  hostedAppV5Firmware              : 391,
  hostedAppV6Firmware              : 392,
  edgeAppFirmware                  : 825,
  jpod2Firmware                    : 399,
  jpod2Config                      : 761,
  ble3Firmware                     : 420,
  vbu3Firmware                     : 419,
  bspModemFirmware                 : 767,
  cellRadioFirmware                : 405,
  obd2Type2Firmware                : 393,
  obd1Type1Firmware                : 377
};

/** Given a device, will return newVersion based on platformID,
    appended with jobID to display Pending Script data
*/
export const getPendingScript  = (device: Device) => {
  return device?.fileHistoryPending?.filter(({platformId}) =>
    platformId === PLATFORM_IDS.config ||
    platformId === PLATFORM_IDS.peg2)?.map?.(result => `[${result.newVersion}] ${result.jobId}`);
};

/** Given a device, will return newVersion based on platformID,
    appended with jobID to display Pending Firmware data
*/
export const getPendingFirmware = (device: Device) => {
  return device?.fileHistoryPending?.filter(({platformId}) =>
    platformId === PLATFORM_IDS.firmware)?.map?.(result => `[${result.newVersion}] ${result.jobId}`);
};

/** Given a device, will return newVersion based on platformID,
    appended with jobID to display Pending JPOD Firmware data
*/
export const getPendingJpodFW = (device: Device, isVbuFwEnabled: boolean = false) => {
  return device?.fileHistoryPending?.filter(({platformId}) =>
    isVbuFwEnabled ? getVbusFirmware(device, platformId)
      : platformId === PLATFORM_IDS.jpodFirmware ||
      platformId === PLATFORM_IDS.jpod2Firmware)?.map?.(result => `[${result.newVersion}] ${result.jobId}`);
};

/** Given a device, will return newVersion based on platformID,
    appended with jobID to display Pending JPOD Config data
*/
export const getPendingJpodConfig = (device: Device, isVbuFwEnabled: boolean = false) => {
  return device?.fileHistoryPending?.filter(({platformId}) =>
    isVbuFwEnabled ? getJpodConfigFirmware(device, platformId)
      : platformId === PLATFORM_IDS.jpodConfig ||
      platformId === PLATFORM_IDS.jpod2Config)?.map?.(result => `[${result.newVersion}] ${result.jobId}`);
};

/**
* @deprecated
* @function getVbusMode
* Given a device, will return OBD or JPOD based on otaCapability
*  to display VBUS Mode
*/
export const getVbusMode = (device: Device) => {
  const vbusModeType = device?.idReport?.device?.idReport?.otaCapability?.protocolMap;
  if (vbusModeType) {
    const arr = Object.keys(vbusModeType);
    const checkOBD = arr.filter(str => (str.includes('OBD')));
    const checkJPOD = arr.filter(str => (str.includes('JPOD')));
    const checkBothTypes = VALUES.every(value => {
      return arr.includes(value);
    });
    if (checkBothTypes) {
      return 'OBD , JPOD';
    }
    if (checkOBD.length !== 0) {
      return 'OBD';
    }
    if (checkJPOD.length !== 0) {
      return 'JPOD';
    }
  }
  return '';
};

/** Given a device, will return newVersion based on platformID,
    appended with jobID to display Pending Hosted App/ EdgeApp data
*/
export const getPendingEdgeHosted = (device: Device) => {
  return device?.fileHistoryPending?.filter(({platformId}) =>
    platformId === PLATFORM_IDS.hostedAppFirmware ||
    platformId === PLATFORM_IDS.hostedAppConfig ||
    platformId === PLATFORM_IDS.hostedAppV2Firmware ||
    platformId === PLATFORM_IDS.hostedAppApnList ||
    platformId === PLATFORM_IDS.hostedAppV3Firmware ||
    platformId === PLATFORM_IDS.hostedAppV4Firmware ||
    platformId === PLATFORM_IDS.hostedAppV5Firmware ||
    platformId === PLATFORM_IDS.hostedAppV6Firmware ||
    platformId === PLATFORM_IDS.hostedAppFirmwareFirstPeripheral ||
    platformId === PLATFORM_IDS.hostedAppConfigFirstPeripheral ||
    platformId === PLATFORM_IDS.hostedAppFirmware2ndPeripheral ||
    platformId === PLATFORM_IDS.hostedAppConfig2ndPeripheral ||
    platformId === PLATFORM_IDS.edgeAppFirmware)?.map?.(result => `[${result.newVersion}] ${result.jobId}`);
};

export const getVersionByPlatformId = (device: Device, platformId) => {
  const fileVersions = device?.extensions?.fileVersions;
  if ((_.isUndefined(fileVersions)) || (!Array.isArray(fileVersions))) {
    return '';
  }
  if (fileVersions) {
    return device?.extensions?.fileVersions?.filter(({platform}) =>
      platform === platformId ||
      platform === platformId)?.map?.(result => result.version);
  }
};

/** Given a device, will return current version based on platformID,
    and display JPOD Firmware data
*/
export const getJpodFirmware = (device: Device, isVbuFwEnabled: boolean = false) => {
  const fileVersions = device?.extensions?.fileVersions;
  if ((_.isUndefined(fileVersions)) || (!Array.isArray(fileVersions))) {
    return '';
  }
  if (fileVersions) {
    return device?.extensions?.fileVersions?.filter(({platform}) =>
      isVbuFwEnabled ? getVbusFirmware(device, platform) : platform === PLATFORM_IDS.jpodFirmware ||
      platform === PLATFORM_IDS.jpod2Firmware)?.map?.(result => result.version);
  }
};

/** Given a device, will return current version based on platformID,
    and display JPOD Config data
*/
export const getJpodConfig = (device: Device, isVbuFwEnabled: boolean = false) => {
  const fileVersions = device?.extensions?.fileVersions;
  if ((_.isUndefined(fileVersions)) || (!Array.isArray(fileVersions))) {
    return '';
  }
  if (fileVersions) {
    return device?.extensions?.fileVersions?.filter(({platform}) =>
      isVbuFwEnabled ? getJpodConfigFirmware(device, platform) : platform === PLATFORM_IDS.jpodConfig ||
      platform === PLATFORM_IDS.jpod2Config)?.map?.(result => result.version);
  }
};

/** Given a device, will return current version based on platformID,
    and display EdgeApp Version data
*/
export const getEdgeAppVersion = (device: Device): string => {
  const fileVersions = device?.extensions?.fileVersions;
  if (Array.isArray(fileVersions)) {
    const {
      edgeAppFirmware,
      hostedAppFirmware,
      hostedAppV2Firmware,
      hostedAppV3Firmware,
      hostedAppV4Firmware,
      hostedAppV5Firmware,
      hostedAppV6Firmware
    } = PLATFORM_IDS;
    return String(fileVersions.filter(({platform}) =>
      platform === edgeAppFirmware ||
      platform === hostedAppFirmware ||
      platform === hostedAppV2Firmware ||
      platform === hostedAppV3Firmware ||
      platform === hostedAppV4Firmware ||
      platform === hostedAppV5Firmware ||
      platform === hostedAppV6Firmware
    )?.map?.(result => result.version));
  }
  return '';
};

/**
 * Given a files (current fileHistory), will return scriptName (reportedName) based on the file's platformID.
 * If the requested reportedName is unavailable or not present on the given file it returns an empty string.
 *
 * @param files The files to determine the current fileHistory.
 * @returns A string scriptName.
 */
export const getScriptFileName = (files: GenericObject[]) => {
  const filesWithReportedName = files?.filter(({reportedName}) => !!reportedName);
  const scriptName = filesWithReportedName?.filter(({platformId}) =>
    platformId === PLATFORM_IDS.config ||
    platformId === PLATFORM_IDS.peg2)?.map(item => item?.reportedName)[0];
  return scriptName || '';
};

/**
 * Given a filemeta, will return Script description based on matched platformID,
 * and display Script Description or empty string.
 */
export const getScriptFileDescription = (filemeta: DeviceFiles): string => {
  const ids = [PLATFORM_IDS.config, PLATFORM_IDS.peg2];
  return filemeta?.find(({description, validationMetaData: {platformId} = {}}) =>
    description && ids?.includes(Number(platformId))
  )?.description || '';
};

/**
 * Given a device, will return JPOD/OBD/VBU file version based on device type.
 */
export const getVbusFirmware = (device, platform) => {
  const deviceMode = device?.extensions?.vbusMode;
  if (deviceMode) {
    if (isEqual(deviceMode, VbusFirmwareMode.JPOD)) {
      return platform === PLATFORM_IDS.jpodFirmware || platform === PLATFORM_IDS.jpod2Firmware;
    } else if (isEqual(deviceMode, VbusFirmwareMode.OBD)) {
      return platform === PLATFORM_IDS.obd1Type1Firmware || platform === PLATFORM_IDS.obd2Type2Firmware;
    } else if (isEqual(deviceMode, VbusFirmwareMode.VBU)) {
      return platform === PLATFORM_IDS.vbu3Firmware;
    }
  }
};

/**
 * Given a device, will return JPOD file version based on device type.
 */
export const getJpodConfigFirmware = (device, platform) => {
  const deviceMode = device?.extensions?.vbusMode;
  if (deviceMode) {
    if (isEqual(deviceMode, VbusFirmwareMode.JPOD)) {
      return platform === PLATFORM_IDS.jpodConfig || platform === PLATFORM_IDS.jpod2Config;
    }
  }
};

/**
 * Check the search term has asterisk already than
 * normalize and return else simply add the asterisk in the search and return.
 */
export const checkSearchTerm = (searchTerm: string): string => {
  return hasAsterisk.test(searchTerm) ? normalizeAsterisks(searchTerm) : `*${searchTerm}*`;
};

/**
 * Append the OR key word when the search key word has multiple items with comma or space
 * separated and return it.
 */
export const getMultipleSearchKeywords = (searchKeyword: any) => {
  const searchKeywords = searchKeyword?.trim()?.split(/[ ,]+/) || [];
  const multipleSearchTerms = [];
  if (searchKeywords?.length > 1) {
    searchKeywords?.filter((item: any) => {
      if (item!== '') {
        multipleSearchTerms.push(validateQuery(item));
      }
    });
    return multipleSearchTerms.join(' OR ');
  }
  return checkSearchTerm(searchKeyword);
};

/**
 * Define the regex for asterisk is exist in the start and end of the string.
 */
export const hasAsterisk  = /^\*|\*$/;

/**
 * check the given query has asterisk in the
 * begining. Based on the match return the boolean value.
 */
export const isQueryStartWithAsterisk = (query: string): boolean => {
  const hasAsteriskStart = /^\*/;
  return hasAsteriskStart.test(query);
};

/**
 * check the given query has asterisk in the
 * end. Based on the match return the boolean value.
 */
export const isQueryEndsWithAsterisk = (query: string): boolean => {
  const hasAsteriskEnd = /\*$/;
  return hasAsteriskEnd.test(query);
};

/**
 * Check the given string has astrisk in the beginning
 * and end of the string and return the query accordingly.
 */
export const normalizeAsterisks = (query: string): string => {
  const hasAsteriskStartEnd = /^\*.*\*$/;
  const partialQuery        = query.replaceAll('*', '');
  if (hasAsteriskStartEnd.test(query)) {
    return `*${partialQuery}*`;
  } else if (isQueryStartWithAsterisk(query)) {
    return `*${partialQuery}`;
  } else if (isQueryEndsWithAsterisk(query)) {
    return `${partialQuery}*`;
  }
  return query;
};

/**
 * Check the `isAnyField` is true and query has asterisk in the start or end, if exist
 * than call the normalize method, else simply return the same query. Return the partial
 * query by default when the isAnyField is coming as false.
 */
export const validateQuery = (query: string, isAnyField = true, hasExactMatch = false): string => {
  if (isAnyField) {
    return hasAsterisk.test(query) ? normalizeAsterisks(query) : query;
  } if (hasExactMatch) {
    return query;
  }
  return `*${query.replaceAll('*', '')}*`;
};

/**
 * getHeartBeat method to get the Heart Beat data from
 * crashboxx API and return the result on success. Return
 * empty object when server returns any exceptions.
 */
export const getHeartBeat = async(heartBeatPayload: GenericObject, includeSubGroups: boolean) => {
  try {
    const heartBeatInfo = await loadHeartBeatInfo(heartBeatPayload, includeSubGroups);
    return heartBeatInfo;
  } catch (err) {
    error(err);
    return {};
  }
};

/**
 * Check if groups has inheritable property.
 */
export const hasGroupInherited = ({inheritable}): boolean => inheritable || false;

/**
 * Utility to check the query has ends with asterisk(*) and add slashes 
 * and return the query. 
 */
export const addSlashesToQuery = (query: string): string => {
  return isQueryEndsWithAsterisk(query) ? query.replaceAll('*', '\\*') : query;
};
